// Groups Service - Community Groups/Clubs System

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  increment,
  arrayUnion,
  arrayRemove,
  writeBatch,
  Unsubscribe,
  DocumentData,
  QueryConstraint,
} from '@firebase/firestore';
import { db } from './firebase';
import type {
  Group,
  GroupCreateInput,
  GroupMember,
  GroupMemberRole,
  GroupMemberStatus,
  GroupPost,
  GroupPostCreateInput,
  GroupPostType,
  GroupComment,
  GroupEvent,
  GroupEventCreateInput,
  EventAttendee,
  GroupInvitation,
  GroupJoinRequest,
  GroupWithMembership,
  GroupCardData,
  GroupSearchFilters,
  LinkedPlace,
  PollOption,
} from '../types/groups';

const GROUPS_COLLECTION = 'groups';
const MEMBERS_SUBCOLLECTION = 'members';
const POSTS_SUBCOLLECTION = 'posts';
const COMMENTS_SUBCOLLECTION = 'comments';
const EVENTS_SUBCOLLECTION = 'events';
const INVITATIONS_COLLECTION = 'groupInvitations';
const JOIN_REQUESTS_SUBCOLLECTION = 'joinRequests';

// ============ GROUP CRUD ============

export async function createGroup(
  input: GroupCreateInput,
  userId: string,
  userName: string
): Promise<string> {
  const groupRef = doc(collection(db, GROUPS_COLLECTION));
  const groupId = groupRef.id;

  // Build group data, excluding undefined values (Firestore doesn't accept undefined)
  const groupData: Record<string, unknown> = {
    name: input.name,
    description: input.description,
    shortDescription: input.shortDescription || input.description.slice(0, 100),
    category: input.category,
    tags: input.tags || [],
    visibility: input.visibility,
    joinPolicy: input.joinPolicy,
    linkedPlaces: [],
    memberCount: 1, // Creator
    postCount: 0,
    eventCount: 0,
    createdBy: userId,
    createdByName: userName,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    isActive: true,
  };

  // Only add optional fields if they have values
  if (input.coverImageUrl) groupData.coverImageUrl = input.coverImageUrl;
  if (input.avatarUrl) groupData.avatarUrl = input.avatarUrl;
  if (input.rules && input.rules.length > 0) groupData.rules = input.rules;
  if (input.welcomeMessage) groupData.welcomeMessage = input.welcomeMessage;

  const batch = writeBatch(db);

  // Create group
  batch.set(groupRef, groupData);

  // Add creator as member
  const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
  const memberData: Omit<GroupMember, 'id'> = {
    groupId,
    userId,
    displayName: userName,
    role: 'creator',
    status: 'active',
    joinedAt: serverTimestamp() as unknown as Timestamp,
    lastActiveAt: serverTimestamp() as unknown as Timestamp,
  };
  batch.set(memberRef, memberData);

  await batch.commit();
  return groupId;
}

export async function getGroup(groupId: string): Promise<Group | null> {
  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  const groupSnap = await getDoc(groupRef);

  if (!groupSnap.exists()) {
    return null;
  }

  return { id: groupSnap.id, ...groupSnap.data() } as Group;
}

export async function getGroupWithMembership(
  groupId: string,
  userId: string | null
): Promise<GroupWithMembership | null> {
  const group = await getGroup(groupId);
  if (!group) return null;

  let currentUserMember: GroupMember | undefined;
  let isPending = false;

  if (userId) {
    // Check if user is a member
    const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
    const memberSnap = await getDoc(memberRef);
    if (memberSnap.exists()) {
      currentUserMember = { id: memberSnap.id, ...memberSnap.data() } as GroupMember;
    }

    // Check for pending join request
    if (!currentUserMember) {
      const requestsQuery = query(
        collection(db, GROUPS_COLLECTION, groupId, JOIN_REQUESTS_SUBCOLLECTION),
        where('userId', '==', userId),
        where('status', '==', 'pending'),
        limit(1)
      );
      const requestSnap = await getDocs(requestsQuery);
      isPending = !requestSnap.empty;
    }
  }

  return {
    ...group,
    currentUserMember,
    currentUserRole: currentUserMember?.role,
    isMember: !!currentUserMember && currentUserMember.status === 'active',
    isPending,
  };
}

export async function updateGroup(
  groupId: string,
  updates: Partial<GroupCreateInput>
): Promise<void> {
  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  await updateDoc(groupRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteGroup(groupId: string): Promise<void> {
  // In production, you'd want to delete subcollections too
  // For now, just mark as inactive
  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  await updateDoc(groupRef, {
    isActive: false,
    updatedAt: serverTimestamp(),
  });
}

// ============ GROUP DISCOVERY ============

export async function searchGroups(
  filters: GroupSearchFilters,
  limitCount: number = 20,
  lastDoc?: DocumentData
): Promise<Group[]> {
  const constraints: QueryConstraint[] = [
    where('isActive', '==', true),
    where('visibility', 'in', ['public', 'private']), // Exclude secret groups from search
  ];

  if (filters.category) {
    constraints.push(where('category', '==', filters.category));
  }

  if (filters.hasLinkedPlace) {
    // This requires a composite index
    constraints.push(where('linkedPlaces', 'array-contains-any', [
      { placeId: filters.hasLinkedPlace }
    ]));
  }

  constraints.push(orderBy('memberCount', 'desc'));
  constraints.push(limit(limitCount));

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const q = query(collection(db, GROUPS_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  let groups = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Group[];

  // Client-side text search (for simplicity)
  if (filters.query) {
    const queryLower = filters.query.toLowerCase();
    groups = groups.filter(g =>
      g.name.toLowerCase().includes(queryLower) ||
      g.description.toLowerCase().includes(queryLower) ||
      g.tags.some(t => t.toLowerCase().includes(queryLower))
    );
  }

  return groups;
}

export async function getPopularGroups(limitCount: number = 10): Promise<Group[]> {
  // Include both public and private groups (exclude only secret)
  const q = query(
    collection(db, GROUPS_COLLECTION),
    where('isActive', '==', true),
    where('visibility', 'in', ['public', 'private']),
    orderBy('memberCount', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Group[];
}

export async function getGroupsForPlace(placeId: string): Promise<Group[]> {
  // Note: This query requires the linkedPlaces array to contain objects with placeId
  // Alternative: create a separate collection for place-group mappings
  const q = query(
    collection(db, GROUPS_COLLECTION),
    where('isActive', '==', true),
    where('visibility', 'in', ['public', 'private']),
    orderBy('memberCount', 'desc')
  );

  const snapshot = await getDocs(q);
  const groups = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Group[];

  // Filter client-side for now
  return groups.filter(g =>
    g.linkedPlaces.some(lp => lp.placeId === placeId)
  );
}

export async function getUserGroups(userId: string): Promise<Group[]> {
  try {
    // Use collection group query to find all memberships for this user
    // This is more efficient and works with security rules
    const membersQuery = query(
      collectionGroup(db, MEMBERS_SUBCOLLECTION),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );

    const membersSnapshot = await getDocs(membersQuery);
    const groups: Group[] = [];

    // For each membership, get the parent group document
    for (const memberDoc of membersSnapshot.docs) {
      // The path is: groups/{groupId}/members/{memberId}
      const groupRef = memberDoc.ref.parent.parent;
      if (groupRef) {
        const groupSnap = await getDoc(groupRef);
        if (groupSnap.exists() && groupSnap.data().isActive !== false) {
          groups.push({ id: groupSnap.id, ...groupSnap.data() } as Group);
        }
      }
    }

    return groups;
  } catch (err) {
    console.error('[getUserGroups] Collection group query failed, using fallback:', err);

    // Fallback: Query all active groups and check membership individually
    const allGroupsQuery = query(
      collection(db, GROUPS_COLLECTION),
      where('isActive', '==', true)
    );

    const allGroupsSnapshot = await getDocs(allGroupsQuery);
    const groups: Group[] = [];

    for (const groupDoc of allGroupsSnapshot.docs) {
      try {
        const memberRef = doc(db, GROUPS_COLLECTION, groupDoc.id, MEMBERS_SUBCOLLECTION, userId);
        const memberSnap = await getDoc(memberRef);
        if (memberSnap.exists() && memberSnap.data().status === 'active') {
          groups.push({ id: groupDoc.id, ...groupDoc.data() } as Group);
        }
      } catch (memberErr) {
        // Skip this group if we can't check membership
        console.warn(`[getUserGroups] Could not check membership for group ${groupDoc.id}:`, memberErr);
      }
    }

    return groups;
  }
}

// ============ MEMBERSHIP ============

export async function joinGroup(
  groupId: string,
  userId: string,
  userName: string,
  userPhotoUrl?: string
): Promise<{ success: boolean; pending?: boolean }> {
  const group = await getGroup(groupId);
  if (!group) {
    throw new Error('Gruppe nicht gefunden');
  }

  if (group.joinPolicy === 'invite_only') {
    throw new Error('Diese Gruppe ist nur auf Einladung zugaenglich');
  }

  if (group.joinPolicy === 'request') {
    // Create join request
    const requestRef = doc(collection(db, GROUPS_COLLECTION, groupId, JOIN_REQUESTS_SUBCOLLECTION));
    const requestData: Omit<GroupJoinRequest, 'id'> = {
      groupId,
      userId,
      displayName: userName,
      photoURL: userPhotoUrl,
      status: 'pending',
      createdAt: serverTimestamp() as unknown as Timestamp,
    };
    await setDoc(requestRef, requestData);
    return { success: true, pending: true };
  }

  // Open group - add directly
  await addMember(groupId, userId, userName, 'member', userPhotoUrl);
  return { success: true };
}

export async function addMember(
  groupId: string,
  userId: string,
  userName: string,
  role: GroupMemberRole = 'member',
  userPhotoUrl?: string
): Promise<void> {
  const batch = writeBatch(db);

  const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
  const memberData: Omit<GroupMember, 'id'> = {
    groupId,
    userId,
    displayName: userName,
    photoURL: userPhotoUrl,
    role,
    status: 'active',
    joinedAt: serverTimestamp() as unknown as Timestamp,
    lastActiveAt: serverTimestamp() as unknown as Timestamp,
  };
  batch.set(memberRef, memberData);

  // Increment member count
  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  batch.update(groupRef, {
    memberCount: increment(1),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

export async function leaveGroup(groupId: string, userId: string): Promise<void> {
  const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
  const memberSnap = await getDoc(memberRef);

  if (!memberSnap.exists()) {
    return; // Not a member
  }

  const member = memberSnap.data() as GroupMember;
  if (member.role === 'creator') {
    throw new Error('Der Ersteller kann die Gruppe nicht verlassen');
  }

  const batch = writeBatch(db);

  batch.delete(memberRef);

  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  batch.update(groupRef, {
    memberCount: increment(-1),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

export async function updateMemberRole(
  groupId: string,
  userId: string,
  newRole: GroupMemberRole
): Promise<void> {
  const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
  await updateDoc(memberRef, { role: newRole });
}

export async function removeMember(
  groupId: string,
  userId: string
): Promise<void> {
  await leaveGroup(groupId, userId);
}

export async function banMember(
  groupId: string,
  userId: string
): Promise<void> {
  const batch = writeBatch(db);

  const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
  batch.update(memberRef, { status: 'banned' });

  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  batch.update(groupRef, {
    memberCount: increment(-1),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

export async function getGroupMembers(
  groupId: string,
  limitCount: number = 50
): Promise<GroupMember[]> {
  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION),
    where('status', '==', 'active'),
    orderBy('role'),
    orderBy('joinedAt'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as GroupMember[];
}

// ============ JOIN REQUESTS ============

export async function getJoinRequests(groupId: string): Promise<GroupJoinRequest[]> {
  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, JOIN_REQUESTS_SUBCOLLECTION),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as GroupJoinRequest[];
}

export async function approveJoinRequest(
  groupId: string,
  requestId: string,
  reviewerId: string
): Promise<void> {
  const requestRef = doc(db, GROUPS_COLLECTION, groupId, JOIN_REQUESTS_SUBCOLLECTION, requestId);
  const requestSnap = await getDoc(requestRef);

  if (!requestSnap.exists()) {
    throw new Error('Anfrage nicht gefunden');
  }

  const request = requestSnap.data() as GroupJoinRequest;

  const batch = writeBatch(db);

  // Update request
  batch.update(requestRef, {
    status: 'approved',
    reviewedBy: reviewerId,
    reviewedAt: serverTimestamp(),
  });

  // Add as member
  const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, request.userId);
  const memberData: Omit<GroupMember, 'id'> = {
    groupId,
    userId: request.userId,
    displayName: request.displayName,
    photoURL: request.photoURL,
    role: 'member',
    status: 'active',
    joinedAt: serverTimestamp() as unknown as Timestamp,
    lastActiveAt: serverTimestamp() as unknown as Timestamp,
  };
  batch.set(memberRef, memberData);

  // Increment member count
  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  batch.update(groupRef, {
    memberCount: increment(1),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

export async function rejectJoinRequest(
  groupId: string,
  requestId: string,
  reviewerId: string,
  reason?: string
): Promise<void> {
  const requestRef = doc(db, GROUPS_COLLECTION, groupId, JOIN_REQUESTS_SUBCOLLECTION, requestId);
  await updateDoc(requestRef, {
    status: 'rejected',
    reviewedBy: reviewerId,
    reviewedAt: serverTimestamp(),
    rejectionReason: reason,
  });
}

// ============ INVITATIONS ============

export async function inviteUser(
  groupId: string,
  groupName: string,
  inviterId: string,
  inviterName: string,
  inviteeId: string,
  inviteeName: string,
  message?: string
): Promise<string> {
  const inviteRef = doc(collection(db, INVITATIONS_COLLECTION));

  const inviteData: Omit<GroupInvitation, 'id'> = {
    groupId,
    groupName,
    inviterId,
    inviterName,
    inviteeId,
    inviteeName,
    message,
    status: 'pending',
    createdAt: serverTimestamp() as unknown as Timestamp,
  };

  await setDoc(inviteRef, inviteData);
  return inviteRef.id;
}

export async function getUserInvitations(userId: string): Promise<GroupInvitation[]> {
  const q = query(
    collection(db, INVITATIONS_COLLECTION),
    where('inviteeId', '==', userId),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as GroupInvitation[];
}

export async function acceptInvitation(
  invitationId: string,
  userId: string,
  userName: string,
  userPhotoUrl?: string
): Promise<void> {
  const inviteRef = doc(db, INVITATIONS_COLLECTION, invitationId);
  const inviteSnap = await getDoc(inviteRef);

  if (!inviteSnap.exists()) {
    throw new Error('Einladung nicht gefunden');
  }

  const invite = inviteSnap.data() as GroupInvitation;

  if (invite.inviteeId !== userId) {
    throw new Error('Diese Einladung ist nicht fuer dich');
  }

  const batch = writeBatch(db);

  // Update invitation
  batch.update(inviteRef, {
    status: 'accepted',
    respondedAt: serverTimestamp(),
  });

  await batch.commit();

  // Add as member
  await addMember(invite.groupId, userId, userName, 'member', userPhotoUrl);
}

export async function declineInvitation(invitationId: string): Promise<void> {
  const inviteRef = doc(db, INVITATIONS_COLLECTION, invitationId);
  await updateDoc(inviteRef, {
    status: 'declined',
    respondedAt: serverTimestamp(),
  });
}

// ============ POSTS ============

export async function createPost(
  input: GroupPostCreateInput,
  userId: string,
  userName: string,
  userPhotoUrl?: string
): Promise<string> {
  const postRef = doc(collection(db, GROUPS_COLLECTION, input.groupId, POSTS_SUBCOLLECTION));

  // Convert poll options if present
  let pollOptions: PollOption[] | undefined;
  if (input.type === 'poll' && input.pollOptions) {
    pollOptions = input.pollOptions.map((text, idx) => ({
      id: `option_${idx}`,
      text,
      votes: [],
    }));
  }

  // Build post data, excluding undefined values
  const postData: Record<string, unknown> = {
    groupId: input.groupId,
    authorId: userId,
    authorName: userName,
    type: input.type,
    content: input.content || '',
    likeCount: 0,
    likedBy: [],
    commentCount: 0,
    createdAt: serverTimestamp(),
    isPinned: false,
    isAnnouncement: false,
  };

  // Add optional fields only if defined
  if (userPhotoUrl) postData.authorPhotoUrl = userPhotoUrl;
  if (input.imageUrls && input.imageUrls.length > 0) postData.imageUrls = input.imageUrls;
  if (input.sharedPlaceId) postData.sharedPlaceId = input.sharedPlaceId;
  if (input.sharedPlaceName) postData.sharedPlaceName = input.sharedPlaceName;
  if (input.sharedPlaceImageUrl) postData.sharedPlaceImageUrl = input.sharedPlaceImageUrl;
  if (pollOptions) postData.pollOptions = pollOptions;
  if (input.pollEndsAt) postData.pollEndsAt = Timestamp.fromDate(input.pollEndsAt);
  if (input.linkedEventId) postData.linkedEventId = input.linkedEventId;

  const batch = writeBatch(db);
  batch.set(postRef, postData);

  // Increment post count
  const groupRef = doc(db, GROUPS_COLLECTION, input.groupId);
  batch.update(groupRef, {
    postCount: increment(1),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
  return postRef.id;
}

export async function getGroupPosts(
  groupId: string,
  limitCount: number = 20,
  lastDoc?: DocumentData
): Promise<GroupPost[]> {
  const constraints: QueryConstraint[] = [
    orderBy('isPinned', 'desc'),
    orderBy('createdAt', 'desc'),
    limit(limitCount),
  ];

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION),
    ...constraints
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as GroupPost[];
}

export async function likePost(
  groupId: string,
  postId: string,
  userId: string
): Promise<void> {
  const postRef = doc(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId);
  await updateDoc(postRef, {
    likeCount: increment(1),
    likedBy: arrayUnion(userId),
  });
}

export async function unlikePost(
  groupId: string,
  postId: string,
  userId: string
): Promise<void> {
  const postRef = doc(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId);
  await updateDoc(postRef, {
    likeCount: increment(-1),
    likedBy: arrayRemove(userId),
  });
}

export async function deletePost(groupId: string, postId: string): Promise<void> {
  const batch = writeBatch(db);

  const postRef = doc(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId);
  batch.delete(postRef);

  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  batch.update(groupRef, {
    postCount: increment(-1),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

export async function pinPost(groupId: string, postId: string): Promise<void> {
  const postRef = doc(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId);
  await updateDoc(postRef, { isPinned: true });
}

export async function unpinPost(groupId: string, postId: string): Promise<void> {
  const postRef = doc(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId);
  await updateDoc(postRef, { isPinned: false });
}

export async function votePoll(
  groupId: string,
  postId: string,
  optionId: string,
  userId: string
): Promise<void> {
  const postRef = doc(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId);
  const postSnap = await getDoc(postRef);

  if (!postSnap.exists()) {
    throw new Error('Post nicht gefunden');
  }

  const post = postSnap.data() as GroupPost;
  if (!post.pollOptions) {
    throw new Error('Kein Poll vorhanden');
  }

  // Remove user from all options, add to selected
  const updatedOptions = post.pollOptions.map(opt => ({
    ...opt,
    votes: opt.id === optionId
      ? [...opt.votes.filter(v => v !== userId), userId]
      : opt.votes.filter(v => v !== userId),
  }));

  await updateDoc(postRef, { pollOptions: updatedOptions });
}

// ============ COMMENTS ============

export async function createComment(
  groupId: string,
  postId: string,
  userId: string,
  userName: string,
  content: string,
  userPhotoUrl?: string,
  parentCommentId?: string
): Promise<string> {
  const commentRef = doc(collection(
    db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId, COMMENTS_SUBCOLLECTION
  ));

  const commentData: Omit<GroupComment, 'id'> = {
    postId,
    groupId,
    authorId: userId,
    authorName: userName,
    authorPhotoUrl: userPhotoUrl,
    content,
    likeCount: 0,
    likedBy: [],
    createdAt: serverTimestamp() as unknown as Timestamp,
    parentCommentId,
    replyCount: 0,
  };

  const batch = writeBatch(db);
  batch.set(commentRef, commentData);

  // Increment comment count on post
  const postRef = doc(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId);
  batch.update(postRef, {
    commentCount: increment(1),
  });

  // If reply, increment reply count on parent
  if (parentCommentId) {
    const parentRef = doc(
      db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId, COMMENTS_SUBCOLLECTION, parentCommentId
    );
    batch.update(parentRef, {
      replyCount: increment(1),
    });
  }

  await batch.commit();
  return commentRef.id;
}

export async function getPostComments(
  groupId: string,
  postId: string,
  limitCount: number = 50
): Promise<GroupComment[]> {
  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION, postId, COMMENTS_SUBCOLLECTION),
    orderBy('createdAt', 'asc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as GroupComment[];
}

// ============ EVENTS ============

export async function createEvent(
  input: GroupEventCreateInput,
  userId: string,
  userName: string
): Promise<string> {
  const eventRef = doc(collection(db, GROUPS_COLLECTION, input.groupId, EVENTS_SUBCOLLECTION));

  // Build event data, excluding undefined values (Firestore doesn't accept undefined)
  // Note: serverTimestamp() cannot be used inside arrays, so we use Timestamp.now()
  const eventData: Record<string, unknown> = {
    groupId: input.groupId,
    title: input.title,
    description: input.description || '', // Ensure not undefined
    startDate: Timestamp.fromDate(input.startDate),
    isAllDay: input.isAllDay || false,
    attendeeCount: 1, // Organizer
    attendees: [{
      userId,
      displayName: userName,
      status: 'going',
      respondedAt: Timestamp.now(),
    }],
    organizerId: userId,
    organizerName: userName,
    createdAt: serverTimestamp(),
    isCancelled: false,
  };

  // Add optional fields only if they have values (not undefined or empty)
  if (input.coverImageUrl) eventData.coverImageUrl = input.coverImageUrl;
  if (input.places && input.places.length > 0) {
    // Filter out any undefined values in place objects
    eventData.places = input.places.map(p => {
      const place: Record<string, unknown> = {
        placeId: p.placeId,
        placeName: p.placeName,
      };
      if (p.placeImageUrl) place.placeImageUrl = p.placeImageUrl;
      if (p.address) place.address = p.address;
      return place;
    });
  }
  if (input.placeId) eventData.placeId = input.placeId;
  if (input.placeName) eventData.placeName = input.placeName;
  if (input.customLocation) eventData.customLocation = input.customLocation;
  if (input.address) eventData.address = input.address;
  if (input.endDate) eventData.endDate = Timestamp.fromDate(input.endDate);
  if (input.maxAttendees) eventData.maxAttendees = input.maxAttendees;

  const batch = writeBatch(db);
  batch.set(eventRef, eventData);

  const groupRef = doc(db, GROUPS_COLLECTION, input.groupId);
  batch.update(groupRef, {
    eventCount: increment(1),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
  return eventRef.id;
}

export async function getGroupEvents(
  groupId: string,
  includesPast: boolean = false,
  limitCount: number = 20
): Promise<GroupEvent[]> {
  const constraints: QueryConstraint[] = [
    where('isCancelled', '==', false),
    orderBy('startDate', 'asc'),
    limit(limitCount),
  ];

  if (!includesPast) {
    constraints.unshift(where('startDate', '>=', Timestamp.now()));
  }

  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, EVENTS_SUBCOLLECTION),
    ...constraints
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as GroupEvent[];
}

export async function respondToEvent(
  groupId: string,
  eventId: string,
  userId: string,
  userName: string,
  status: 'going' | 'maybe' | 'not_going',
  userPhotoUrl?: string
): Promise<void> {
  const eventRef = doc(db, GROUPS_COLLECTION, groupId, EVENTS_SUBCOLLECTION, eventId);
  const eventSnap = await getDoc(eventRef);

  if (!eventSnap.exists()) {
    throw new Error('Event nicht gefunden');
  }

  const event = eventSnap.data() as GroupEvent;

  // Remove existing response
  const existingIndex = event.attendees.findIndex(a => a.userId === userId);
  const wasGoing = existingIndex >= 0 && event.attendees[existingIndex].status === 'going';

  // Build attendee object without undefined values
  const newAttendee: Record<string, unknown> = {
    userId,
    displayName: userName,
    status,
    respondedAt: Timestamp.now(),
  };
  if (userPhotoUrl) {
    newAttendee.photoURL = userPhotoUrl;
  }

  let updatedAttendees: Record<string, unknown>[];
  if (existingIndex >= 0) {
    updatedAttendees = [...event.attendees] as unknown as Record<string, unknown>[];
    updatedAttendees[existingIndex] = newAttendee;
  } else {
    updatedAttendees = [...event.attendees, newAttendee] as unknown as Record<string, unknown>[];
  }

  // Calculate attendee count change
  const isNowGoing = status === 'going';
  let countDelta = 0;
  if (wasGoing && !isNowGoing) countDelta = -1;
  if (!wasGoing && isNowGoing) countDelta = 1;

  await updateDoc(eventRef, {
    attendees: updatedAttendees,
    attendeeCount: increment(countDelta),
  });
}

export async function cancelEvent(groupId: string, eventId: string): Promise<void> {
  const eventRef = doc(db, GROUPS_COLLECTION, groupId, EVENTS_SUBCOLLECTION, eventId);
  await updateDoc(eventRef, {
    isCancelled: true,
    updatedAt: serverTimestamp(),
  });
}

// Extended event type with group info for cross-group display
export interface GroupEventWithGroup extends GroupEvent {
  groupName: string;
  groupAvatarUrl?: string;
}

/**
 * Get all upcoming events from groups the user is a member of
 */
export async function getUserUpcomingEvents(userId: string, limitCount: number = 30): Promise<GroupEventWithGroup[]> {
  // First get user's groups
  const userGroups = await getUserGroups(userId);
  if (userGroups.length === 0) return [];

  const allEvents: GroupEventWithGroup[] = [];
  const now = Timestamp.now();

  // Fetch events from each group in parallel
  const eventPromises = userGroups.map(async (group) => {
    try {
      const eventsQuery = query(
        collection(db, GROUPS_COLLECTION, group.id, EVENTS_SUBCOLLECTION),
        where('isCancelled', '==', false),
        where('startDate', '>=', now),
        orderBy('startDate', 'asc'),
        limit(10)
      );
      const snapshot = await getDocs(eventsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        groupName: group.name,
        groupAvatarUrl: group.avatarUrl || group.coverImageUrl,
      })) as GroupEventWithGroup[];
    } catch (err) {
      console.warn(`[getUserUpcomingEvents] Could not fetch events for group ${group.id}:`, err);
      return [];
    }
  });

  const results = await Promise.all(eventPromises);
  results.forEach(events => allEvents.push(...events));

  // Sort by start date and limit
  return allEvents
    .sort((a, b) => {
      const aDate = a.startDate instanceof Timestamp ? a.startDate.toMillis() : new Date(a.startDate).getTime();
      const bDate = b.startDate instanceof Timestamp ? b.startDate.toMillis() : new Date(b.startDate).getTime();
      return aDate - bDate;
    })
    .slice(0, limitCount);
}

/**
 * Get all upcoming events from public groups
 */
export async function getPublicUpcomingEvents(limitCount: number = 30): Promise<GroupEventWithGroup[]> {
  // Get public groups
  const publicGroupsQuery = query(
    collection(db, GROUPS_COLLECTION),
    where('isActive', '==', true),
    where('visibility', '==', 'public'),
    limit(50)
  );
  const groupsSnapshot = await getDocs(publicGroupsQuery);
  const publicGroups = groupsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Group[];

  if (publicGroups.length === 0) return [];

  const allEvents: GroupEventWithGroup[] = [];
  const now = Timestamp.now();

  // Fetch events from each public group in parallel
  const eventPromises = publicGroups.map(async (group) => {
    try {
      const eventsQuery = query(
        collection(db, GROUPS_COLLECTION, group.id, EVENTS_SUBCOLLECTION),
        where('isCancelled', '==', false),
        where('startDate', '>=', now),
        orderBy('startDate', 'asc'),
        limit(5)
      );
      const snapshot = await getDocs(eventsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        groupName: group.name,
        groupAvatarUrl: group.avatarUrl || group.coverImageUrl,
      })) as GroupEventWithGroup[];
    } catch (err) {
      console.warn(`[getPublicUpcomingEvents] Could not fetch events for group ${group.id}:`, err);
      return [];
    }
  });

  const results = await Promise.all(eventPromises);
  results.forEach(events => allEvents.push(...events));

  // Sort by start date and limit
  return allEvents
    .sort((a, b) => {
      const aDate = a.startDate instanceof Timestamp ? a.startDate.toMillis() : new Date(a.startDate).getTime();
      const bDate = b.startDate instanceof Timestamp ? b.startDate.toMillis() : new Date(b.startDate).getTime();
      return aDate - bDate;
    })
    .slice(0, limitCount);
}

// ============ LINKED PLACES ============

export async function linkPlace(
  groupId: string,
  placeId: string,
  placeName: string,
  placeImageUrl: string | undefined,
  userId: string,
  role: 'primary' | 'regular' = 'regular'
): Promise<void> {
  // Build linked place object without undefined values
  const linkedPlace: Record<string, unknown> = {
    placeId,
    placeName,
    role,
    addedAt: Timestamp.now(),
    addedBy: userId,
  };
  if (placeImageUrl) {
    linkedPlace.placeImageUrl = placeImageUrl;
  }

  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  await updateDoc(groupRef, {
    linkedPlaces: arrayUnion(linkedPlace),
    updatedAt: serverTimestamp(),
  });
}

export async function unlinkPlace(groupId: string, placeId: string): Promise<void> {
  const group = await getGroup(groupId);
  if (!group) return;

  const updatedPlaces = group.linkedPlaces.filter(lp => lp.placeId !== placeId);

  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  await updateDoc(groupRef, {
    linkedPlaces: updatedPlaces,
    updatedAt: serverTimestamp(),
  });
}

// ============ REAL-TIME SUBSCRIPTIONS ============

export function subscribeToGroup(
  groupId: string,
  callback: (group: Group | null) => void
): Unsubscribe {
  const groupRef = doc(db, GROUPS_COLLECTION, groupId);
  return onSnapshot(groupRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({ id: snapshot.id, ...snapshot.data() } as Group);
    } else {
      callback(null);
    }
  });
}

export function subscribeToGroupPosts(
  groupId: string,
  callback: (posts: GroupPost[]) => void,
  limitCount: number = 20
): Unsubscribe {
  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, POSTS_SUBCOLLECTION),
    orderBy('isPinned', 'desc'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  return onSnapshot(q, (snapshot) => {
    const posts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as GroupPost[];
    callback(posts);
  });
}

export function subscribeToGroupEvents(
  groupId: string,
  callback: (events: GroupEvent[]) => void
): Unsubscribe {
  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, EVENTS_SUBCOLLECTION),
    where('isCancelled', '==', false),
    where('startDate', '>=', Timestamp.now()),
    orderBy('startDate', 'asc'),
    limit(10)
  );

  return onSnapshot(q, (snapshot) => {
    const events = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as GroupEvent[];
    callback(events);
  });
}
